import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';

export class ItemFlatNode {
  displayName?: string;
  level?: number;
  resourceId?: string;
  shared?: boolean;
  expandable?: boolean;
}

export class ItemNode {
  children!: ItemNode[];
  displayName!: string;
  resourceId?: string;
  shared?: boolean;
}

@Component({
  selector: 'app-dialog-select-eu-share',
  templateUrl: './dialog-select-eu-share.component.html',
  styleUrls: ['./dialog-select-eu-share.component.scss']
})
export class DialogSelectEuShareComponent {
  flatNodeMap = new Map<ItemFlatNode, ItemNode>();
  nestedNodeMap = new Map<ItemNode, ItemFlatNode>();
  selectedParent: ItemFlatNode | null = null;
  treeControl: FlatTreeControl<ItemFlatNode>;
  treeFlattener!: MatTreeFlattener<ItemNode, ItemFlatNode>;
  dataSource!: MatTreeFlatDataSource<ItemNode, ItemFlatNode>;
  checklistSelection = new SelectionModel<ItemFlatNode>(true);
  dataChange = new BehaviorSubject<ItemNode[]>([]);
  dataService: ItemNode[] = [];

  ngOnInit() {
    for (let i = 0; i < this.treeControl.dataNodes.length; i++) {
      if (this.treeControl.dataNodes[i].shared) {
        this.itemSelectionToggle(this.treeControl.dataNodes[i]);
        const parentNode = this.getParentNode(this.treeControl.dataNodes[i]);
        if (parentNode) {
          this.treeControl.expand(parentNode);
          const menuNode = this.getParentNode(parentNode);
          if (menuNode) this.treeControl.expand(menuNode);
        }
      }
    }
  }

  constructor(
    public dialogRef: MatDialogRef<DialogSelectEuShareComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data) {
      this.initialize(data);
    }

    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<ItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.dataChange.subscribe((data) => {
      this.dataSource.data = data;
    });
  }

  initialize(data: any) {
    const userEus = {
      displayName: 'dialog_select_eu_share_my_eus',
      children: data.filter((d: { eUs: { isEuUser: boolean }[] }) => d.eUs.some((eu: { isEuUser: boolean }) => eu.isEuUser))
    };

    const otherEus = {
      displayName: 'dialog_select_eu_share_other_eus',
      children: data.filter((d: { eUs: { isEuUser: boolean }[] }) => d.eUs.every((eu: { isEuUser: boolean }) => !eu.isEuUser))
    };

    const dataTree = [userEus, otherEus];

    this.dataService = this.buildFileTree(dataTree);
    // Notify the change.
    this.dataChange.next(this.dataService);
  }

  getLevel = (node: ItemFlatNode) => node.level || 0;

  getChildren = (node: ItemNode): any[] => node.children;

  hasNoContent = (_: number, _nodeData: ItemFlatNode) => _nodeData.displayName === '';

  onClickBack() {
    this.dialogRef.close();
  }

  onClickAdd() {
    return this.checklistSelection?.selected.filter((x) => x.resourceId != null && x.level != (0 || 1));
  }

  // Filtrer lors du changement du code
  nameChanged(target: any) {
    let filteredData: any[] = [];

    if (this.dataService.length > 0 && target?.value?.length > 1) {
      let headers = this.dataService.filter((ar) => {
        let children = ar.children.filter((c: ItemNode) => {
          return c.children.some((eu) => eu.displayName?.toLowerCase().includes(target?.value?.toLowerCase()));
        });

        return children.length > 0;
      });

      headers.map((header) => {
        let children: any[] = [];
        let filteredMembers = header.children.filter((member) => member.children.some((eu) => eu.displayName?.toLowerCase().includes(target?.value?.toLowerCase())));
        let newHeader = {
          displayName: header.displayName,
          children: children
        };

        filteredMembers.map((member) => {
          let filteredEus = member.children.filter((eu) => eu.displayName?.toLowerCase().includes(target?.value?.toLowerCase()));
          let newMember = {
            displayName: member.displayName,
            children: filteredEus
          };
          newHeader.children.push(newMember);
        });

        filteredData.push(newHeader);
      });

      this.dataChange.next(filteredData);
      this.treeControl.expandAll();
    }

    if (this.dataService.length > 0 && target?.value?.length < 2) {
      this.dataChange.next(this.dataService);
    }
  }

  isExpandable = (node: ItemFlatNode) => node.expandable || false;

  transformer = (node: ItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.displayName === node.displayName ? existingNode : new ItemFlatNode();
    flatNode.displayName = node.displayName;
    flatNode.level = level;
    flatNode.resourceId = node.resourceId != node.displayName ? node.resourceId : undefined;
    flatNode.shared = node.shared;
    flatNode.expandable = !!node.children?.length;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  leafItemSelectionToggle(node: ItemFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  checkAllParentsSelection(node: ItemFlatNode): void {
    let parent: ItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  checkRootNodeSelection(node: ItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  getParentNode(node: ItemFlatNode): ItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel && currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (currentLevel && this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  descendantsAllSelected(node: ItemFlatNode): boolean {
    if (node.displayName != 'dialog_select_eu_share_other_eus') {
      const descendants = this.treeControl.getDescendants(node);
      const descAllSelected =
        descendants.length > 0 &&
        descendants.every((child) => {
          return this.checklistSelection.isSelected(child);
        });
    }
    return false;
  }

  descendantsPartiallySelected(node: ItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  itemSelectionToggle(node: ItemFlatNode): void {
    if (node.displayName != 'dialog_select_eu_share_other_eus') {
      this.checklistSelection.toggle(node);
      const descendants = this.treeControl.getDescendants(node);
      this.checklistSelection.isSelected(node) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

      descendants.forEach((child) => this.checklistSelection.isSelected(child));
      this.checkAllParentsSelection(node);
    }
  }

  hasChild = (_: number, _nodeData: ItemFlatNode) => _nodeData.expandable;

  buildFileTree(obj: { [key: string]: any }, level: number = 0): ItemNode[] {
    return Object.keys(obj).reduce<ItemNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new ItemNode();

      if (value?.euId) {
        node.resourceId = value?.euId;
      } else if (value?.memberId) {
        node.resourceId = value?.memberId;
      }

      node.displayName = value?.displayName;
      node.shared = value?.shared;

      if (value != null && level == 0) {
        if (value.children) {
          node.children = this.buildFileTree(value.children, level + 1);
        }
      }

      if (value != null && level == 1) {
        if (typeof value === 'object') {
          if (value?.eUs) {
            node.children = this.buildFileTree(value?.eUs, level + 1);
          }
        }
      }
      return accumulator.concat(node);
    }, []);
  }
}
